import { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import PageMeta from "../seo/PageMeta";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query, where, Timestamp } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import Menu from "../sections/Menu";
import Footer from "../sections/Footer";

const allowedEmails = [
  "iwona.bendig@egodziny.pl",
  "krzysztof.warda@egodziny.pl",
  "dominik.pogorzelski@egodziny.pl",
  "wiktor.spryszynski@egodziny.pl",
];

const formatLandingPath = (path) => {
  if (!path) return "-";
  if (path === "/") return "Strona główna";
  if (path.startsWith("/blog/")) return `Blog: ${path.replace("/blog/", "")}`;
  if (path === "/blog") return "Blog (lista)";
  if (path.startsWith("/register")) return "Formularz rejestracji";
  if (path === "/cennik") return "Cennik";
  if (path === "/funkcje") return "Funkcje";
  if (path === "/o-nas") return "O nas";
  if (path === "/kontakt") return "Kontakt";
  if (path === "/privacy") return "Polityka prywatności";
  if (path === "/regulamin") return "Regulamin";
  if (path === "/case-study") return "Case Study";
  if (path.startsWith("/case-study/")) return `Case Study: ${path.replace("/case-study/", "")}`;
  if (path === "/praca-tymczasowa-i-outsourcing") return "Praca tymczasowa i outsourcing";
  if (path === "/inzynieria-i-budownictwo") return "Inżynieria i budownictwo";
  if (path === "/app") return "App Store";
  return path;
};

const PERIOD_OPTIONS = [
  { value: "7", label: "Ostatnie 7 dni" },
  { value: "14", label: "Ostatnie 14 dni" },
  { value: "30", label: "Ostatnie 30 dni" },
  { value: "90", label: "Ostatnie 90 dni" },
  { value: "all", label: "Wszystko" },
];

export default function AdminPageViews() {
  const [pageViews, setPageViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [period, setPeriod] = useState("30");
  const [filterBlog, setFilterBlog] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate("/login/blog?redirect=/admin-panel/page-views");
        return;
      }
      if (!allowedEmails.includes(currentUser.email)) {
        alert("Brak dostępu");
        navigate("/");
        return;
      }
      setIsAuthorized(true);
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    const loadPageViews = async () => {
      setLoading(true);
      let q;
      if (period === "all") {
        q = query(collection(db, "page_views"), orderBy("createdAt", "desc"));
      } else {
        const daysAgo = new Date();
        daysAgo.setDate(daysAgo.getDate() - parseInt(period, 10));
        q = query(
          collection(db, "page_views"),
          where("createdAt", ">=", Timestamp.fromDate(daysAgo)),
          orderBy("createdAt", "desc")
        );
      }
      const snapshot = await getDocs(q);
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setPageViews(rows);
      setLoading(false);
    };

    if (isAuthorized) {
      loadPageViews();
    }
  }, [isAuthorized, period]);

  const stats = useMemo(() => {
    const byPath = {};
    const uniqueVisitors = new Set();
    const byDay = {};

    for (const view of pageViews) {
      const path = view.path || "(brak)";
      if (!byPath[path]) {
        byPath[path] = { count: 0, uniqueVisitors: new Set() };
      }
      byPath[path].count += 1;
      if (view.visitorId) {
        byPath[path].uniqueVisitors.add(view.visitorId);
        uniqueVisitors.add(view.visitorId);
      }

      const day = view.timestamp ? view.timestamp.slice(0, 10) : null;
      if (day) {
        if (!byDay[day]) byDay[day] = 0;
        byDay[day] += 1;
      }
    }

    const pages = Object.entries(byPath)
      .map(([path, data]) => ({
        path,
        label: formatLandingPath(path),
        views: data.count,
        uniqueVisitors: data.uniqueVisitors.size,
        isBlog: path.startsWith("/blog"),
      }))
      .sort((a, b) => b.views - a.views);

    const dailyViews = Object.entries(byDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, count]) => ({ day, count }));

    return {
      totalViews: pageViews.length,
      uniqueVisitors: uniqueVisitors.size,
      pages,
      dailyViews,
    };
  }, [pageViews]);

  const filteredPages = filterBlog ? stats.pages.filter((p) => p.isBlog) : stats.pages;

  return (
    <>
      <PageMeta path="/admin-panel/page-views" />
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <Menu />
      <main style={{ padding: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "1.5rem" }}>
          <h1 style={{ margin: 0 }}>Statystyki wyświetleń stron</h1>
          <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
            <button className="button-default btn" onClick={() => navigate("/admin-panel")}>
              Formularze
            </button>
            <button className="button-default btn" onClick={() => navigate("/admin-panel/kalkulator")}>
              Wejścia kalkulator
            </button>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              Okres:
              <select
                className="login-input"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                style={{ maxWidth: "200px" }}
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={filterBlog}
                onChange={(e) => setFilterBlog(e.target.checked)}
              />
              Tylko blog
            </label>
          </div>
        </div>

        {loading ? (
          <p>Ładowanie...</p>
        ) : (
          <>
            {/* Summary cards */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "1.5rem" }}>
              <div style={{ background: "#f5f7fa", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "12px 16px", minWidth: "150px" }}>
                <div style={{ fontSize: "12px", color: "#666" }}>Wyświetleń łącznie</div>
                <div style={{ fontSize: "28px", fontWeight: 700, color: "#1a73e8" }}>{stats.totalViews}</div>
              </div>
              <div style={{ background: "#f5f7fa", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "12px 16px", minWidth: "150px" }}>
                <div style={{ fontSize: "12px", color: "#666" }}>Unikalni odwiedzający</div>
                <div style={{ fontSize: "28px", fontWeight: 700, color: "#1a73e8" }}>{stats.uniqueVisitors}</div>
              </div>
              <div style={{ background: "#f5f7fa", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "12px 16px", minWidth: "150px" }}>
                <div style={{ fontSize: "12px", color: "#666" }}>Wyświetleń bloga</div>
                <div style={{ fontSize: "28px", fontWeight: 700, color: "#1a73e8" }}>
                  {stats.pages.filter((p) => p.isBlog).reduce((sum, p) => sum + p.views, 0)}
                </div>
              </div>
            </div>

            {/* Daily chart (simple text-based) */}
            {stats.dailyViews.length > 0 && (
              <div style={{ marginBottom: "1.5rem", background: "#f9fafb", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", overflowX: "auto" }}>
                <h3 style={{ margin: "0 0 12px 0", fontSize: "16px" }}>Wyświetlenia dziennie</h3>
                <div style={{ display: "flex", alignItems: "flex-end", gap: "4px", height: "120px" }}>
                  {stats.dailyViews.map((day) => {
                    const maxCount = Math.max(...stats.dailyViews.map((d) => d.count), 1);
                    const height = Math.max((day.count / maxCount) * 100, 4);
                    return (
                      <div key={day.day} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "1 1 0", minWidth: "24px" }}>
                        <span style={{ fontSize: "10px", color: "#666", marginBottom: "4px" }}>{day.count}</span>
                        <div
                          style={{ width: "100%", maxWidth: "32px", height: `${height}px`, background: "#1a73e8", borderRadius: "3px 3px 0 0" }}
                          title={`${day.day}: ${day.count} wyświetleń`}
                        />
                        <span style={{ fontSize: "9px", color: "#999", marginTop: "4px", writingMode: "vertical-rl", transform: "rotate(180deg)", maxHeight: "50px", overflow: "hidden" }}>
                          {day.day.slice(5)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Pages table */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Strona</th>
                    <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Ścieżka</th>
                    <th style={{ textAlign: "right", padding: "10px", borderBottom: "1px solid #ddd" }}>Wyświetlenia</th>
                    <th style={{ textAlign: "right", padding: "10px", borderBottom: "1px solid #ddd" }}>Unikalni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPages.map((page) => (
                    <tr key={page.path} style={page.isBlog ? { backgroundColor: "#f0f7ff" } : undefined}>
                      <td style={{ padding: "10px", borderBottom: "1px solid #eee", fontWeight: 500 }}>
                        {page.label}
                      </td>
                      <td style={{ padding: "10px", borderBottom: "1px solid #eee", fontFamily: "monospace", fontSize: "13px", color: "#666" }}>
                        {page.path}
                      </td>
                      <td style={{ padding: "10px", borderBottom: "1px solid #eee", textAlign: "right", fontWeight: 600 }}>
                        {page.views}
                      </td>
                      <td style={{ padding: "10px", borderBottom: "1px solid #eee", textAlign: "right" }}>
                        {page.uniqueVisitors}
                      </td>
                    </tr>
                  ))}
                  {filteredPages.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ padding: "10px" }}>Brak danych.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
      <Footer />
    </>
  );
}
